import { useState } from 'react';
import type { HotelFilters, HotelSortOption, StarRating } from '../types/hotel';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';

interface HotelFiltersProps {
  filters: HotelFilters;
  onFiltersChange: (filters: HotelFilters) => void;
  sortBy: HotelSortOption;
  onSortChange: (sort: HotelSortOption) => void;
  availableAmenities: string[];
  className?: string;
}

interface FilterSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function FilterSection({ title, isOpen, onToggle, children }: FilterSectionProps) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-gray-900"
      >
        {title}
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>
      {isOpen && <div className="pb-3">{children}</div>}
    </div>
  );
}

export default function HotelFilters({
  filters,
  onFiltersChange,
  sortBy,
  onSortChange,
  availableAmenities,
  className = '',
}: HotelFiltersProps) {
  const [expandedSections, setExpandedSections] = useState({
    starRating: true,
    price: true,
    guestRating: false,
    amenities: false,
    propertyType: false,
    bookingOptions: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleStarRatingToggle = (star: number) => {
    const starRating = star as StarRating;
    const newStars = filters.starRating.includes(starRating)
      ? filters.starRating.filter(s => s !== starRating)
      : [...filters.starRating, starRating];

    onFiltersChange({
      ...filters,
      starRating: newStars,
    });
  };

  const handlePriceChange = (type: 'min' | 'max', value: number) => {
    onFiltersChange({
      ...filters,
      priceRange: {
        ...filters.priceRange,
        [type]: value,
      },
    });
  };

  const handleGuestRatingChange = (value: number) => {
    onFiltersChange({
      ...filters,
      guestRating: {
        ...filters.guestRating,
        min: value,
      },
    });
  };

  const handleAmenityToggle = (amenity: string) => {
    const newAmenities = filters.amenities.includes(amenity)
      ? filters.amenities.filter(a => a !== amenity)
      : [...filters.amenities, amenity];
    
    onFiltersChange({
      ...filters,
      amenities: newAmenities,
    });
  };

  const handlePropertyTypeToggle = (type: string) => {
    const newTypes = filters.propertyType.includes(type as any)
      ? filters.propertyType.filter(t => t !== type)
      : [...filters.propertyType, type as any];
    
    onFiltersChange({
      ...filters,
      propertyType: newTypes,
    });
  };

  const handleBookingOptionToggle = (option: 'payLater' | 'freeBreakfast') => {
    onFiltersChange({
      ...filters,
      bookingOptions: {
        ...filters.bookingOptions,
        [option]: !filters.bookingOptions[option],
      },
    });
  };

  const handleAccessibilityToggle = (option: 'wheelchairAccessible' | 'accessibleParking') => {
    onFiltersChange({
      ...filters,
      accessibility: {
        ...filters.accessibility,
        [option]: !filters.accessibility[option],
      },
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      starRating: [5, 4, 3, 2, 1],
      priceRange: { min: 0, max: 1000 },
      guestRating: { min: 0 },
      amenities: [],
      propertyType: [],
      freeCancellation: false,
      bookingOptions: {
        payLater: false,
        freeBreakfast: false,
      },
      accessibility: {
        wheelchairAccessible: false,
        accessibleParking: false,
      },
    });
  };

  const hasActiveFilters =
    filters.starRating.length < 5 ||
    filters.priceRange.min > 0 ||
    filters.priceRange.max < 1000 ||
    filters.guestRating.min > 0 ||
    filters.amenities.length > 0 ||
    filters.propertyType.length > 0 ||
    filters.bookingOptions.payLater ||
    filters.bookingOptions.freeBreakfast ||
    filters.accessibility.wheelchairAccessible ||
    filters.accessibility.accessibleParking;

  const activeFilterCount =
    (filters.starRating.length < 5 ? 1 : 0) +
    (filters.priceRange.min > 0 ? 1 : 0) +
    (filters.priceRange.max < 1000 ? 1 : 0) +
    (filters.guestRating.min > 0 ? 1 : 0) +
    (filters.amenities.length > 0 ? 1 : 0) +
    (filters.propertyType.length > 0 ? 1 : 0) +
    (filters.bookingOptions.payLater ? 1 : 0) +
    (filters.bookingOptions.freeBreakfast ? 1 : 0) +
    (filters.accessibility.wheelchairAccessible ? 1 : 0) +
    (filters.accessibility.accessibleParking ? 1 : 0);

  return (
    <div className={`bg-white rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-900">Filters</span>
          {hasActiveFilters && (
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-purple-600 hover:text-purple-700"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Sort Options */}
      <div className="py-3 border-b border-gray-200">
        <label className="text-xs font-medium text-gray-600 mb-2 block">Sort by</label>
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value as HotelSortOption)}
          className="input-field"
        >
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
          <option value="rating-desc">Guest Rating</option>
          <option value="stars-desc">Star Rating</option>
          <option value="reviews-desc">Most Reviewed</option>
        </select>
      </div>

      {/* Star Rating Filter */}
      <FilterSection
        title="Star Rating"
        isOpen={expandedSections.starRating}
        onToggle={() => toggleSection('starRating')}
      >
        <div className="flex flex-wrap gap-2">
          {[5, 4, 3, 2, 1].map(star => (
            <button
              key={star}
              onClick={() => handleStarRatingToggle(star)}
              className={`px-3 py-1.5 rounded-lg border transition-colors ${
                filters.starRating.includes(star as StarRating)
                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {'★'.repeat(star)}{'☆'.repeat(5 - star)}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Price Filter */}
      <FilterSection
        title={`Price: $${filters.priceRange.min} - $${filters.priceRange.max}`}
        isOpen={expandedSections.price}
        onToggle={() => toggleSection('price')}
      >
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Min: ${filters.priceRange.min}</span>
              <span>Max: ${filters.priceRange.max}</span>
            </div>
            <input
              type="range"
              min="0"
              max="1000"
              step="25"
              value={filters.priceRange.max}
              onChange={(e) => handlePriceChange('max', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
      </FilterSection>

      {/* Guest Rating Filter */}
      <FilterSection
        title={`Guest Rating: ${filters.guestRating.min}+`}
        isOpen={expandedSections.guestRating}
        onToggle={() => toggleSection('guestRating')}
      >
        <div className="space-y-2">
          {[9, 8, 7, 6].map(rating => (
            <label key={rating} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="guestRating"
                checked={filters.guestRating.min === rating}
                onChange={() => handleGuestRatingChange(rating)}
                className="text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">{rating}+ rating</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Amenities Filter */}
      {availableAmenities.length > 0 && (
        <FilterSection
          title={`Amenities (${filters.amenities.length} selected)`}
          isOpen={expandedSections.amenities}
          onToggle={() => toggleSection('amenities')}
        >
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {availableAmenities.slice(0, 10).map(amenity => (
              <label key={amenity} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.amenities.includes(amenity)}
                  onChange={() => handleAmenityToggle(amenity)}
                  className="rounded text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">{amenity}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Property Type Filter */}
      <FilterSection
        title="Property Type"
        isOpen={expandedSections.propertyType}
        onToggle={() => toggleSection('propertyType')}
      >
        <div className="space-y-2">
          {['Hotel', 'Resort', 'Boutique', 'Aparthotel', 'Villa'].map(type => (
            <label key={type} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.propertyType.includes(type as any)}
                onChange={() => handlePropertyTypeToggle(type)}
                className="rounded text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">{type}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Booking Options */}
      <FilterSection
        title="Booking Options"
        isOpen={expandedSections.bookingOptions}
        onToggle={() => toggleSection('bookingOptions')}
      >
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.bookingOptions.payLater}
              onChange={() => handleBookingOptionToggle('payLater')}
              className="rounded text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">Pay later</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.bookingOptions.freeBreakfast}
              onChange={() => handleBookingOptionToggle('freeBreakfast')}
              className="rounded text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">Free breakfast</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.accessibility.wheelchairAccessible}
              onChange={() => handleAccessibilityToggle('wheelchairAccessible')}
              className="rounded text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">Wheelchair accessible</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.accessibility.accessibleParking}
              onChange={() => handleAccessibilityToggle('accessibleParking')}
              className="rounded text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">Accessible parking</span>
          </label>
        </div>
      </FilterSection>
    </div>
  );
}
